import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import pick from 'lodash/pick';
import { tradeInVehicleTypes } from '../../../../../lib/apiKeys';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import units from '../../../../../lib/units';
import types from '../../../../../types';
import makeInputValue from '../../../../../lib/makeInputValue';
import numericInput from '../../../../../lib/numericInput';

const vehicleTradeInOptions = (vehicles, t) => vehicles
  .filter(vehicle => !vehicle.isElectric)
  .map(vehicle => ({ value: vehicle.index, display: t('Vehicle {{index}}', vehicle) }));

const manufacturingAssumptions = (isMetric, t) => {
  const gCo2PerMile = 53;
  const gCo2PerDistance = isMetric ? Math.round(gCo2PerMile / units.KM_PER_MI) : gCo2PerMile;
  const miLifespan = 150000;
  const distanceLifespan = isMetric ? units.miToKm(miLifespan) : miLifespan;
  return {
    gCo2PerDistance,
    distanceUnit: isMetric ? t('km', { ns: 'units' }) : t('mi', { ns: 'units' }),
    distanceLifespan: numericInput.format(distanceLifespan),
  };
};

const PurchaseGreenElectricity = ({
  t,
  vehicles,
  isMetric,
  hybridTradeInVehicle,
  updateTradeInVehicle,
  widgetSource,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I will trade in')}
        <InlineSelect
          ariaLabel={t('vehicle')}
          options={vehicleTradeInOptions(vehicles, t)}
          inputValue={makeInputValue(hybridTradeInVehicle.index, 1)}
          onChange={value => updateTradeInVehicle(tradeInVehicleTypes.HYBRID, value)}
        />
        {t('which has a fuel economy of {{efficiencyDisplay}} {{fuelEfficiencyUnit}} and buy a more efficient hybrid-electric vehicle that uses', hybridTradeInVehicle)}
        <InlineInput
          apiKey="input_takeaction_hybrid_vehicle_mpg_new"
        />
        {units.fuelEfficiencyUnit(t)(isMetric, false)}.
      </p>
      <p>
        {t('I will drive this vehicle')}
        <InlineInput
          maxLength={8}
          inputProps={{
            placeholder: hybridTradeInVehicle.annualDistance ||
              hybridTradeInVehicle.defaultDistance,
          }}
          apiKey="input_takeaction_hybrid_vehicle_miles_new"
        />
        {t('{{distanceUnit}} per year.', { distanceUnit: isMetric ? t('km') : t('mi') })}
      </p>
      <p>
        {t('I can sell my current vehicle for')}
        <InlineInput
          currency
          maxLength={9}
          apiKey="input_takeaction_hybrid_vehicle_sell"
        />
        {t('and purchase a new one for')}
        <InlineInput
          currency
          maxLength={9}
          apiKey="input_takeaction_hybrid_vehicle_purchase"
        />.
      </p>
    </div>
    {widgetSource !== 'ontario' && <div className="action-details__facts">
      {t('Americans are choosing greener vehicles--the average fuel efficiency of cars on the road in the U.S. has gone up steadily for the past 10 years. Don\'t get left behind in a gas guzzler!')}
    </div>}
    {widgetSource === 'ontario' && <div className="action-details__facts">
      {t('Ontarians are choosing greener vehicles--the average fuel efficiency of cars on the road in the Canada has gone up steadily for the past 10 years. Don\'t get left behind in a gas guzzler!')}
    </div>}
    {widgetSource !== 'ontario' &&
      <div className="action-details__links">
        <a
          href="http://www.coolcalifornia.org/funding-wizard#search/home+electricity//false/none/false/1/12"
          target="_blank"
          rel="noopener noreferrer"
        >{t('Find rebates and incentives for this action')}</a>
      </div>
    }
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Price of gasoline:')}
          <InlineInput
            currency
            apiKey="input_takeaction_assumption_price_of_gasoline"
          />
          {t('per {{volumeUnit}}', { volumeUnit: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        <li
          dangerouslySetInnerHTML={{
            __html: t('{{gCo2PerDistance}} grams CO<sub>2</sub>eq per {{distanceUnit}} from vehicle manufacturing (8 metric tons per vehicle / {{distanceLifespan}} {{distanceUnit}} lifespan)', manufacturingAssumptions(isMetric, t)),
          }}
        />
        <li>
          {t('20% Well-to-pump emissions. This includes emissions from the fuel used to find, extract, transport and refine crude oil into gasoline.')}
        </li>
        {widgetSource !== 'ontario' &&
          <li>
            {t('A number of rebates are available for alternative fuel vehicles from state, federal and local agencies. We assume $2,000 rebate per vehicle is available at the time of purchase.')}
          </li>
        }
        <li>
          {t('Emissions from road construction and maintenance, and other government-related emissions are not included.')}
        </li>
      </ul>
    </div>
  </div>
);

PurchaseGreenElectricity.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  hybridTradeInVehicle: types.Vehicle.isRequired,
  vehicles: types.Vehicles.isRequired,
  isMetric: PropTypes.bool.isRequired,
  updateTradeInVehicle: PropTypes.func.isRequired,
  widgetSource: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'vehicles',
  'isMetric',
  'hybridTradeInVehicle',
  'widgetSource',
].reduce((props, selector) => ({
  ...props,
  [selector]: s[selector](state, ownProps),
}), {});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'updateTradeInVehicle',
  ]),
  dispatch,
);

export default wrap(
  withT('hybridVehicle'),
  connect(mapStateToProps, mapDispatchToProps)(PurchaseGreenElectricity),
);
